import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { hasConsented, setConsent } from '@/lib/cookie-consent'
import { cn } from '@/lib/utils'

/**
 * Cookie consent banner — shown at the bottom of the viewport on public
 * pages (landing, login, signup) when the visitor hasn't made a consent
 * choice yet. Styled to match the landing page visual language, not the
 * app's shadcn theme.
 *
 *   - Accept: enables PostHog analytics on public pages
 *   - Decline: PostHog never loads (no scripts, no cookies)
 *
 * Never shown inside /app — authenticated users are covered by the ToS.
 *
 * Mounted inside the router (RootLayout) rather than beside it, so the
 * route check re-runs on every navigation. Reading `window.location`
 * once on mount left the banner stranded on screen when a visitor
 * landed on a public page and then client-navigated into /app.
 */
export function CookieConsentBanner() {
  const { pathname } = useLocation()
  const isAppRoute = pathname.startsWith('/app')

  const [choiceMade, setChoiceMade] = useState(() => hasConsented())
  const [animateIn, setAnimateIn] = useState(false)

  // Re-read storage on navigation so a choice made in another tab — or a
  // logout back onto a public page — is reflected without a reload.
  useEffect(() => {
    setChoiceMade(hasConsented())
  }, [pathname])

  useEffect(() => {
    if (isAppRoute || choiceMade) {
      setAnimateIn(false)
      return
    }
    const timer = setTimeout(() => setAnimateIn(true), 50)
    return () => clearTimeout(timer)
  }, [isAppRoute, choiceMade])

  const handleAccept = () => {
    setConsent('accepted')
    setAnimateIn(false)
    setTimeout(() => setChoiceMade(true), 200)
  }

  const handleDecline = () => {
    setConsent('declined')
    setAnimateIn(false)
    setTimeout(() => setChoiceMade(true), 200)
  }

  if (isAppRoute || choiceMade) return null

  return (
    <div
      className={cn(
        'fixed bottom-4 left-4 z-50 w-[calc(100%-2rem)] max-w-sm transition-all duration-200 ease-out',
        animateIn ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      )}
    >
      <div className="rounded-xl border border-[#ECECEC] bg-white p-5 shadow-lg">
        <p className="text-sm text-[#3F4651] leading-relaxed">
          We use cookies to understand how you use BidContext and improve the experience.{' '}
          See our{' '}
          <a
            href="/cookies"
            className="text-[#6024EE] underline underline-offset-2 hover:text-[#4E14C9]"
            target="_blank"
            rel="noreferrer"
          >
            Cookies policy
          </a>
          {' '}and{' '}
          <a
            href="/privacy"
            className="text-[#6024EE] underline underline-offset-2 hover:text-[#4E14C9]"
            target="_blank"
            rel="noreferrer"
          >
            Privacy policy
          </a>
          .
        </p>
        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            onClick={handleDecline}
            className="px-4 py-2 text-sm font-medium text-[#0F1419] bg-white border border-[#ECECEC] rounded-md hover:border-[#0F1419] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6024EE] focus-visible:ring-offset-2"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="px-4 py-2 text-sm font-medium text-white bg-[#2A2D31] rounded-md hover:bg-[#1E2124] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6024EE] focus-visible:ring-offset-2"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  )
}
